<template>
  <v-container fluid>
    <v-card>
      <v-card-subtitle>
        Summary of Teachers Payrolls
      </v-card-subtitle>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-radio-group row v-model="dateSelectionMode" label="Date selection mode" @change="loadReport">
              <v-radio value="single" label="Single date" />
              <v-radio value="range" label="Date Range" />
            </v-radio-group>
          </v-col>
        </v-row>
        <v-row v-if="dateSelectionMode == 'single'">
          <v-col cols="12">
              <v-text-field
                v-model="endDate"
                label="Report Date"
                prepend-icon="mdi-calendar"
                type="date"
                clearable
                :rules="[rules.required]"
                @change="loadReport"
              ></v-text-field>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.required]"
              @change="loadReport"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="6">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              type="date"
              clearable
              :rules="[rules.dateRange(startDate, endDate)]"
              @change="loadReport"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

<v-sheet v-if="teachers && teachers.length > 0" class="d-flex justify-end mt-10">
  <v-btn @click="print('teacher-payroll-report')">Print</v-btn>
</v-sheet>

    <v-card id="teacher-payroll-report" class="mt-10" v-if="teachers && teachers.length > 0">
      <div v-if="dateSelectionMode == 'single'" class="d-flex justify-space-between pa-5">
        <v-btn elevation="2" @click="changeSingleDate(-1)" icon><v-icon>mdi-arrow-left</v-icon></v-btn>
        <div class="text-h6">{{endDate | formatDate}}</div>
        <v-btn elevation="2" @click="changeSingleDate(1)" icon><v-icon>mdi-arrow-right</v-icon></v-btn>
      </div>
      <div v-else class="d-flex justify-center pa-5">
        <div class="text-h6">{{startDate | formatDate}} - {{endDate | formatDate}}</div>
      </div>
      <v-card-title class="justify-center"> Payroll Summary </v-card-title>
      <v-card-subtitle style="text-align: center">
        Found {{ teachers.length }} teachers
      </v-card-subtitle>
      <v-card-text>
        <v-expansion-panels v-model="selectedExpandPanel" class="mb-10">
          <v-expansion-panel v-for="teacher in teachers" :key="teacher.id">
            <v-expansion-panel-header >
              <div class="d-flex justify-space-between">
                <span>{{teacher.firstName}} {{teacher.lastName}}</span>
                <span class="pr-10">{{teacher.totalHrs | formatDecimal}} hrs | {{teacher.totalPay | currency}}</span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table>
                <thead>
                  <tr>
                    <td>Date</td>
                    <td>Course(s)</td>
                    <td>Hour(s)</td>
                    <td>Total Pay</td>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="teacherPayroll in teacher.payrolls" :key="teacherPayroll.date">
                    <td>{{teacherPayroll.payrollDate | formatDate}}</td>
                    <td>
                      <a @click="showSchedule(teacherPayroll.payrollDate, teacherPayroll.courseScheduleId)">
                        {{teacherPayroll.course.name}}
                      </a>
                    </td>
                    <td>{{teacherPayroll.payableHours}} @ {{teacherPayroll.rate | currency}}/hr</td>
                    <td>{{teacherPayroll.totalAmount | currency}}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-overlay :absolute="true" :value="overlay" class="text-center">
          <v-progress-circular
            :width="10"
            :size="100"
            color="red"
            indeterminate
          />
          <br />
          <div class="ma-5 text-center">Loading...</div>
        </v-overlay>
      </v-card-text>
    </v-card>

    <v-dialog v-if="selectedScheduleId" v-model="scheduleDialog">
      <v-sheet>
          <course-schedule
            titleColor="primary"
            :scheduleId="selectedScheduleId"
            :scheduleDate="selectedDate"
            :allowDelete="false"
            :viewCourse="true"
            :key="selectedScheduleId"
          />

          <div class="d-flex justify-center">
            <v-btn class="my-5" text color="secondary" @click="scheduleDialog = false">
              Close
            </v-btn>
          </div>
        </v-sheet>
    </v-dialog>

  </v-container>
</template>

<script>
import { rules } from "@/store/util/rules.js";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
const CourseSchedule = () => import("@/components/course/CourseSchedule.vue");

export default {
  data() {
    return {
      scheduleDialog: false,
      selectedScheduleId: null,
      selectedDate: null,
      overlay: true,
      teachers: [],
      courses: [],
      printing: false,
      rules,
    };
  },
  filters: {
    formatDate(date) {
      return moment(date).format("ddd, MMM DD, YYYY");
    },
    formatDateTime(date) {
      return moment.utc(date).local().format("ddd, MMM DD, YYYY h:mm a");
    },
    formatDecimal(val){
      if(val){
        return val.toFixed(2);
      }
      else{
        return 0;
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "isAppLoading", "globalCenter", "myCenters"]),
    ...mapGetters('reports', ["reportStartDate", "reportEndDate", "reportDateSelectionMode", "selectedPanel"]),
    dateSelectionMode: {
      get(){
        return this.reportDateSelectionMode;
      },
      set(value){
        this.setReportDateSelectionMode(value);
      }
    },
    startDate:{
      get(){
        return this.reportStartDate;
      },
      set(value){
        this.setReportStartDate(value);
      }
    },
    endDate:{
      get(){
        return this.reportEndDate;
      },
      set(value){
        this.setReportEndDate(value);
      }
    },
    selectedExpandPanel:{
      get(){
        return this.selectedPanel;
      },
      set(value){
        this.setSelectedPanel(value);
      }
    }
  },
  mounted() {
    if(this.startDate == null){
      this.startDate = moment().startOf("month").format("YYYY-MM-DD");
    }

    if(this.endDate == null){
      this.endDate = moment().format("YYYY-MM-DD");
    }

    this.loadReport();
  },
  methods: {
    ...mapActions(["addErrorMessage", "clearMessage"]),
    ...mapActions('reports', ["setReportDateSelectionMode", "setReportEndDate", "setReportStartDate", "setSelectedPanel"]),

    async loadReport(){
      let start = this.startDate;
      const end = this.endDate;

      if(this.dateSelectionMode == 'single'){
        start = end;
      }
      else{
        start = this.startDate;
      }

      const params = {
        start,
        end
      }

      this.overlay =true;
      
      return this.$axios('/payroll/teacher-report', {params})
        .then((resp) => {
          const report = resp.data;

          console.log(report);

          this.teachers = report.teachers;
          this.courses = report.courses;

          this.teachers.forEach(t => {
            t.totalHrs = 0;
            t.totalPay = 0;
            t.payrolls = [];
          })

          report.teacherPayrolls.forEach( payroll => {
            const teacherId = payroll.teacherId;
            const courseId = payroll.courseId;
            const teacher = this.teachers.find(t => t.id == teacherId);
            const course = this.courses.find(c => c.id == courseId);
            teacher.totalHrs += (payroll.payableHours ? payroll.payableHours:0);
            teacher.totalPay += (payroll.totalAmount ? payroll.totalAmount:0);
            payroll.course = course;
            teacher.payrolls.push(payroll)
          })

          //sort teachers
          this.teachers.sort( (t1, t2) => t1.firstName < t2.firstName ? -1:1)

          //sort payrolls
          this.teachers.forEach(t => {
            t.payrolls.sort( (p1, p2) => moment(p1.payrollDate).format('YYYYMMDD') - moment(p2.payrollDate).format('YYYYMMDD') )
          })
            
            this.overlay =false;
            return this.report;
        })
    },
    changeSingleDate(change){
      this.endDate = moment(this.endDate).add(change, 'days').format('YYYY-MM-DD')
      this.loadReport();
    },
    print(componentId){
      this.printing = true;

      // console.log(`printing component: ${componentId}`)
      this.$htmlToPaper(componentId, null, () => {
        this.printing = false;
      });
    },
    showSchedule(date, scheduleId){
      this.selectedDate = date;
      this.selectedScheduleId = scheduleId;
      this.scheduleDialog = true;
    }
  },
  components: {
    CourseSchedule
  },
};
</script>